import { IWithExpandedAllProps, withExpanded } from '../../../../shared/lib/hoc/WithExpanded'
import { ColumnsType } from 'antd/lib/table'
import { Space } from 'antd'
import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../app/store/hooks/Redux'
import { IDetailedInfoFilter } from '../../model/types/DetailedInfoFilter.interface'
import { selectDetailedInfoFilters } from '../../model/domainOverviewSlice'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../../../shared/types/TableResponse.type'
import { IBpPaginationProps } from '../../../../shared/components/cards/pagination/BpPagination'
import { SorterResult } from 'antd/lib/table/interface'
import { useSorter } from '../../../../shared/lib/hooks/UseSorter'
import {
  AdvertisingAffiliatesItems,
  CommaLists,
  GetResearchAdvertisingAffiliatesApiArg,
  useGetResearchAdvertisingAffiliatesQuery,
  usePostAdOverviewHideAffiliateMutation,
} from '../../../../app/api'
import { LinksList } from '../../../ad-overview/widgets/link-list/LinksList'
import { useTranslation } from 'react-i18next'
import { AffiliateIdCell } from '../../ui/cells/AffiliateIdCell'
import { RedirectLinksCountCell } from '../../ui/cells/RedirectLinksCountCell'
import { PageUrlCell } from '../../ui/cells/PageUrlCell'
import { LandingPageCell } from '../../../../shared/ui/cells/LandingPageCell'
import { EditAffiliateCell } from '../../ui/cells/EditAffiliateCell'
import { HiddenActionCell } from '../../ui/cells/HiddenActionCell'
import clsx from 'clsx'
import styles from './AffiliatesTable.module.scss'

type AffiliateRow = AdvertisingAffiliatesItems & { additionalRedirects?: string[][] }

const Table = ({ onToggleExpand, isExpandAll, expandedRowKeys }: IWithExpandedAllProps) => {
  const { t } = useTranslation()
  const detailedInfoFilter = useAppSelector<IDetailedInfoFilter>(selectDetailedInfoFilters)
  const {
    project,
    site,
    country,
    engine_id,
    dateRange: date,
    keyword,
    has_brand,
    page_url,
    show_hidden,
    reversed_filters,
  } = detailedInfoFilter

  const [queryParams, setQueryParams] =
    useState<CommaLists<GetResearchAdvertisingAffiliatesApiArg>>()
  const { data, isLoading, isFetching, refetch } = useGetResearchAdvertisingAffiliatesQuery(
    queryParams as GetResearchAdvertisingAffiliatesApiArg,
    { skip: !project || queryParams == undefined },
  )
  const [editAffiliate] = usePostAdOverviewHideAffiliateMutation()

  useEffect(() => {
    if (!project) return

    setQueryParams({
      page: 1,
      'per-page': 10,
      projectId: +project,
      site,
      countryId: country ? +country : undefined,
      engineId: +engine_id,
      date,
      keywordId: keyword || undefined,
      hasBrand: has_brand,
      landingId: page_url || undefined,
      showHidden: show_hidden,
      reversedFilters: (reversed_filters || undefined) as unknown as string[],
    })
  }, [detailedInfoFilter])

  const columns: ColumnsType<AdvertisingAffiliatesItems> = [
    {
      title: t('table.columns.affiliateId'),
      dataIndex: 'affiliate_id',
      key: 'affiliate_id',
      render: (_: string, record) => <AffiliateIdCell {...record} />,
    },
    {
      title: t('table.columns.redirectLinks'),
      dataIndex: 'redirects_count',
      key: 'redirects_count',
      render: (_: string, record) => (
        <RedirectLinksCountCell
          {...record}
          expandedRowKeys={expandedRowKeys}
          onToggleExpand={onToggleExpand}
        />
      ),
    },
    {
      title: t('table.columns.page'),
      dataIndex: 'page',
      key: 'page',
      render: (_: string, record) => <PageUrlCell {...record} />,
    },
    {
      title: t('table.columns.landingPage'),
      dataIndex: 'link_screen_url',
      key: 'link_screen_url',
      render: (_: string, record) => <LandingPageCell {...record} />,
    },
    {
      title: t('table.columns.firstFound'),
      dataIndex: 'first_found',
      key: 'first_found',
      width: '150px',
      sorter: true,
    },
    {
      title: t('table.columns.lastFound'),
      dataIndex: 'last_found',
      key: 'last_found',
      width: 150,
      sorter: true,
    },
    {
      key: 'actions',
      render: (_: AdvertisingAffiliatesItems, record) => (
        <Space>
          <EditAffiliateCell {...record} />
          <HiddenActionCell
            {...record}
            onToggle={() => {
              if (!project) return
              editAffiliate({
                body: {
                  id: record.publisher_id,
                  is_hidden: !record.is_hidden,
                },
              }).then(() => refetch())
            }}
          />
        </Space>
      ),
      width: 176,
      fixed: 'right',
    },
  ]

  const handleTableChange = (
    { current, pageSize }: IBpPaginationProps,
    sorter?: SorterResult<AdvertisingAffiliatesItems> | SorterResult<AdvertisingAffiliatesItems>[],
  ) => {
    if (queryParams) {
      setQueryParams({
        ...queryParams,
        page: current,
        'per-page': pageSize,
        sort: useSorter(sorter),
      })
    }
  }

  return (
    <BpTable
      expandable={{
        expandedRowRender: (record: AffiliateRow) => (
          <LinksList
            additionalRedirects={record.additionalRedirects}
            className={clsx(styles.expandedRowContent)}
            redirects={record.redirects}
          />
        ),
        expandedRowKeys: isExpandAll
          ? data?.data?.items?.map((item) => item.affiliate_id)
          : expandedRowKeys,
        showExpandColumn: false,
      }}
      columns={columns}
      data={data as TTableResponse<AdvertisingAffiliatesItems>}
      dataSource={data?.data?.items}
      loading={isLoading || isFetching}
      rowClassName={(e) => (e.is_hidden ? 'disabled' : '')}
      rowKey={(record) => record.affiliate_id}
      onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
    ></BpTable>
  )
}

export const AffiliatesTable = withExpanded(Table)
